import { AnimatePresence, motion } from "motion/react";
import { Bot, Sparkles } from "lucide-react";
import { SPRING_SMOOTH } from "@/lib/motion";
import {
  KitFocusable,
  KitTooltip,
  KitTooltipTrigger,
} from "@/app/routes/dashboard/components/kit/Tooltip";
import type { WorkshopLivePerson } from "@/types/realtime.type";
import type { WorkshopPresentPerson } from "@/types/workshop.type";
import { cn } from "@/utils/helpers";
import { PeopleStack, namesList } from "./PeopleStack";

/**
 * Who is in a shared thread right now — the viewer included, like a shared
 * doc — plus the assistant, lit while it is working.
 */
export function ThreadPresenceBar({
  present,
  replying,
}: {
  present: WorkshopPresentPerson[];
  replying: boolean;
}) {
  return (
    <div className="flex shrink-0 items-center gap-2" aria-label="In this thread now">
      <PeopleStack
        people={present}
        size={22}
        max={5}
        label={
          present.length > 0 ? `Here now: ${namesList(present, 4)}` : "Nobody else is here"
        }
      />
      <span
        className={cn(
          "grid size-[22px] shrink-0 place-items-center border transition-colors",
          replying
            ? "border-co-accent bg-co-accent text-co-on-accent"
            : "border-co-line2 bg-transparent text-co-faint",
        )}
        title={replying ? "The assistant is working" : "The assistant"}
        aria-label={replying ? "The assistant is working" : "The assistant"}
      >
        <Sparkles className={cn("size-3", replying && "animate-pulse")} aria-hidden />
      </span>
    </div>
  );
}

/**
 * The assistant's mark under the newest message it has read: everything up to
 * here reached it, whether or not it replies. Only ever on one message.
 */
export function AssistantSeenBadge() {
  const label = "Seen by Workshop Agent";
  return (
    <KitTooltipTrigger delay={200}>
      <KitFocusable>
        <span className="ws-seen-badge" role="img" aria-label={label}>
          <Bot aria-hidden />
        </span>
      </KitFocusable>
      <KitTooltip>{label}</KitTooltip>
    </KitTooltipTrigger>
  );
}

/** Three dots that pulse in turn: someone is writing. */
function TypingDots() {
  return (
    <span className="ws-typing-dots" aria-hidden>
      <span />
      <span />
      <span />
    </span>
  );
}

/**
 * An item of the typing line rises out from behind the composer (the line
 * clips it at its bottom edge) and sinks back into it when it ends.
 */
const RISE_FROM_COMPOSER = {
  initial: { y: 22, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  exit: { y: 22, opacity: 0 },
  transition: SPRING_SMOOTH,
} as const;

/**
 * The WhatsApp line above the composer, on the composer's own column: who is
 * typing, their marks then their names. The assistant's work shows in the
 * thread itself (its live reply), never here.
 */
export function ThreadTypingLine({ typing }: { typing: WorkshopLivePerson[] }) {
  return (
    // Always mounted, so the composer below never jumps when it fills or empties.
    <div className="ws-typing-line" aria-live="polite">
      <AnimatePresence initial={false}>
        {typing.length > 0 ? (
          <motion.span key="people" className="ws-typing-item" {...RISE_FROM_COMPOSER}>
            <PeopleStack people={typing} size={16} max={3} />
            <span className="ws-typing-text">
              {namesList(typing)} {typing.length === 1 ? "is" : "are"} typing
            </span>
            <TypingDots />
          </motion.span>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
